import { Box } from "@mui/material";
import { useEffect, useRef } from "react";

/**
 * 全粒子の振動位相が揃って 0 になり、Fibonacci 配置の完全球体が
 * 再現されるまでのフレーム数。60fps 想定で約 9s。
 *
 * 整数倍周波数 (k_i ∈ ℤ) を使うので、`sin(k_i * 2π * t / T)` は
 * `t = 0, T/2, T, 3T/2, ...` で全 i について同時に 0 となる。
 * 結果として PERIOD_FRAMES の半周期ごとに球体がリセットされる。
 * 内外 2 層は同じ PERIOD_FRAMES を共有するので同時にリセットされる。
 */
const PERIOD_FRAMES = 540;

/**
 * 各粒子の周波数倍率 k_i の候補。整数のみ。低速寄りに重み付け。
 */
const FREQ_CHOICES = [1, 1, 1, 1, 1, 2, 2, 3];

/** 接ベクトルに加えるランダム回転の最大角 (ラジアン)。 */
const TANGENT_PERTURB = 0.35;

interface Particle {
  hx: number;
  hy: number;
  hz: number;
  tx: number;
  ty: number;
  tz: number;
  amplitude: number;
  freq: number;
  brightness: number;
  color: string;
}

interface LayerSpec {
  /** 粒子数 */
  count: number;
  /** 投影時の球半径係数 (ベース半径への乗数) */
  radiusFactor: number;
  /** 振幅 (ラジアン) のベース値 */
  amplitudeBase: number;
  /** 振幅の赤道方向ボーナス上限 */
  amplitudeRange: number;
  /** 流れ場の主軸 (粒子接ベクトルの基準) */
  flowAxis: { x: number; y: number; z: number };
  /** 粒子サイズ計算: depth (0..1) を引数に [size_min, size_max] を返す */
  sizeAt: (depth: number) => number;
  /** 粒子色: ホーム位置から色文字列 (`#rrggbb` など) を返す */
  colorFor: (hx: number, hy: number, hz: number) => string;
  /**
   * 振動位相の進行倍率。1.0 で基準速度、0.667 で 2/3 速度。
   * 全粒子の周波数倍率 k_i は整数なので、speedScale が有理数の場合も
   * 各レイヤは固有の周期で確実にリセットされる (ただし内/外層の同期点は LCM)。
   */
  speedScale: number;
}

/** 内層のグラデーション (上から下へ ティール → インディゴ)。冷色のみ。 */
const INNER_COLOR_BANDS: readonly string[] = [
  "#7fe0d4", // top: light teal
  "#5ee0d4",
  "#4895ef",
  "#5b8eff",
  "#7c8aff",
  "#9b8bff", // bottom: light indigo
];

const OUTER_LAYER: LayerSpec = {
  count: 3500,
  radiusFactor: 1.0,
  amplitudeBase: 0.2,
  amplitudeRange: 0.55,
  flowAxis: { x: 0.0, y: 0.95, z: 0.31 },
  // 内層の 1.5 倍サイズ
  sizeAt: (d) => 0.525 + d * 1.275,
  colorFor: () => "#ffffff",
  // 移動速度を 2/3 に
  speedScale: 2 / 3,
};

const INNER_LAYER: LayerSpec = {
  count: 1800,
  radiusFactor: 2 / 3,
  amplitudeBase: 0.18,
  amplitudeRange: 0.45,
  // 別軸にして外層との動きの違いを出す
  flowAxis: { x: 0.31, y: 0.95, z: 0.0 },
  sizeAt: (d) => 0.35 + d * 0.85,
  colorFor: (_hx, hy) => {
    const t = (1 - hy) / 2; // 0 (top) .. 1 (bottom)
    const idx = Math.min(INNER_COLOR_BANDS.length - 1, Math.max(0, Math.floor(t * INNER_COLOR_BANDS.length)));
    return INNER_COLOR_BANDS[idx] ?? "#ffffff";
  },
  speedScale: 1.0,
};

/**
 * Fibonacci 球面配置 + 各粒子の独自軌道パラメータを生成。
 *
 * 接ベクトルは「`home × flowAxis` を主方向とし、接平面内で
 * ±TANGENT_PERTURB のランダム回転を加えた向き」。
 * 振動運動 v(t) = home·cos(θ) + tangent·sin(θ) は |v| = 1 を保つので
 * 粒子は常に単位球面上に乗り続け、球体形状は崩れない。
 *
 * 末尾で粒子を色文字列でソートし、描画時の fillStyle 変更回数を
 * 色の種類数 (≦ INNER_COLOR_BANDS.length) に抑える。
 */
function buildLayer(spec: LayerSpec): Particle[] {
  const out: Particle[] = [];
  const phi = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < spec.count; i++) {
    const hy = 1 - (i / (spec.count - 1)) * 2;
    const rxy = Math.sqrt(1 - hy * hy);
    const theta = phi * i;
    const hx = Math.cos(theta) * rxy;
    const hz = Math.sin(theta) * rxy;

    let primaryX = hy * spec.flowAxis.z - hz * spec.flowAxis.y;
    let primaryY = hz * spec.flowAxis.x - hx * spec.flowAxis.z;
    let primaryZ = hx * spec.flowAxis.y - hy * spec.flowAxis.x;
    let plen = Math.hypot(primaryX, primaryY, primaryZ);
    if (plen < 1e-3) {
      primaryX = 1;
      primaryY = 0;
      primaryZ = 0;
      plen = 1;
    }
    primaryX /= plen;
    primaryY /= plen;
    primaryZ /= plen;
    const biX = hy * primaryZ - hz * primaryY;
    const biY = hz * primaryX - hx * primaryZ;
    const biZ = hx * primaryY - hy * primaryX;

    const perturb = (Math.random() - 0.5) * 2 * TANGENT_PERTURB;
    const cs = Math.cos(perturb);
    const sn = Math.sin(perturb);
    const tx = primaryX * cs + biX * sn;
    const ty = primaryY * cs + biY * sn;
    const tz = primaryZ * cs + biZ * sn;

    const dotAxis = hx * spec.flowAxis.x + hy * spec.flowAxis.y + hz * spec.flowAxis.z;
    const equatorial = Math.sqrt(Math.max(0, 1 - dotAxis * dotAxis));
    const amplitude = spec.amplitudeBase + equatorial * spec.amplitudeRange + Math.random() * 0.2;

    const k = FREQ_CHOICES[Math.floor(Math.random() * FREQ_CHOICES.length)] ?? 1;
    const sign = Math.random() < 0.5 ? -1 : 1;
    const brightness = 0.55 + Math.random() * 0.45;
    const color = spec.colorFor(hx, hy, hz);

    out.push({ hx, hy, hz, tx, ty, tz, amplitude, freq: k * sign, brightness, color });
  }
  out.sort((a, b) => (a.color < b.color ? -1 : a.color > b.color ? 1 : 0));
  return out;
}

/**
 * `references/background-1.gif` を模した、回転する 2 層パーティクル球の背景アニメーション。
 *
 *  - Canvas 2D + requestAnimationFrame
 *  - DPR 対応で高解像度ディスプレイでも粒が潰れない
 *  - 各粒子は「ホーム位置」と「接ベクトル方向」を持ち、
 *    `pos(t) = home·cos(θ) + tangent·sin(θ)` (θ = A·sin(k·2π·t/T)) で振動する。
 *    |pos| = 1 が常に保たれるので球体形状は崩れない。
 *  - 接ベクトルは滑らかな流れ場 (`home × flowAxis` + 小摂動) なので、
 *    隣接粒子は同方向に流れ、筋状/縞状の綺麗なパターンが出る。
 *  - k_i が整数なので t = T/2, T, ... では全粒子の θ が同時に 0 となり、
 *    Fibonacci 配置の完全球体が周期的に再現される。
 *  - 2 層構成: 外側 (白, radius=1.0) + 内側 (冷色グラデ, radius=0.62)。
 *    両層は同じ PERIOD_FRAMES で同期し、リセット瞬間が一致する。
 *  - 描画順は内 → 外。外側の白い殻越しに内側の冷色コアが透けて見える。
 *  - prefers-reduced-motion で回転と振動を停止
 *  - タブ非表示中はループを停止しリソースを節約
 */
export function ParticleSphereBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const innerParticles = buildLayer(INNER_LAYER);
    const outerParticles = buildLayer(OUTER_LAYER);

    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const rotationSpeedY = reduceMotion ? 0 : 0.0022;
    const rotationSpeedX = reduceMotion ? 0 : 0.0009;
    const oscillationActive = !reduceMotion;

    let rafId = 0;
    let frame = 0;
    let running = true;

    const drawLayer = (
      particles: Particle[],
      screenRadius: number,
      sizeAt: (d: number) => number,
      cosY: number,
      sinY: number,
      cosX: number,
      sinX: number,
      cx: number,
      cy: number,
      phaseBase: number,
    ) => {
      let lastColor = "";
      for (const p of particles) {
        if (p.color !== lastColor) {
          ctx.fillStyle = p.color;
          lastColor = p.color;
        }
        const theta = p.amplitude * Math.sin(p.freq * phaseBase);
        const ct = Math.cos(theta);
        const st = Math.sin(theta);

        const px = p.hx * ct + p.tx * st;
        const py = p.hy * ct + p.ty * st;
        const pz = p.hz * ct + p.tz * st;

        const x1 = px * cosY - pz * sinY;
        const z1 = px * sinY + pz * cosY;
        const y1 = py;
        const y2 = y1 * cosX - z1 * sinX;
        const z2 = y1 * sinX + z1 * cosX;

        const screenX = cx + x1 * screenRadius;
        const screenY = cy + y2 * screenRadius;

        const depth = (z2 + 1) / 2;
        const alpha = (0.12 + depth * 0.78) * p.brightness;
        const size = sizeAt(depth);
        const half = size * 0.5;

        ctx.globalAlpha = alpha > 1 ? 1 : alpha;
        ctx.fillRect(screenX - half, screenY - half, size, size);
      }
    };

    const render = () => {
      if (!running) return;
      frame += 1;

      ctx.clearRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;
      const baseRadius = Math.min(width, height) * 0.34;

      const angY = frame * rotationSpeedY;
      const angX = 0.18 + Math.sin(frame * rotationSpeedX) * 0.22;
      const cosY = Math.cos(angY);
      const sinY = Math.sin(angY);
      const cosX = Math.cos(angX);
      const sinX = Math.sin(angX);

      const phaseBase = oscillationActive ? (frame / PERIOD_FRAMES) * Math.PI * 2 : 0;

      // 内層 (冷色) を先に描画 → 外層 (白) でその上を覆う。
      // 結果として外側の白い殻越しに内部の冷色コアが透けて見える。
      drawLayer(
        innerParticles,
        baseRadius * INNER_LAYER.radiusFactor,
        INNER_LAYER.sizeAt,
        cosY,
        sinY,
        cosX,
        sinX,
        cx,
        cy,
        phaseBase * INNER_LAYER.speedScale,
      );
      drawLayer(
        outerParticles,
        baseRadius * OUTER_LAYER.radiusFactor,
        OUTER_LAYER.sizeAt,
        cosY,
        sinY,
        cosX,
        sinX,
        cx,
        cy,
        phaseBase * OUTER_LAYER.speedScale,
      );

      ctx.globalAlpha = 1;

      rafId = requestAnimationFrame(render);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(rafId);
      } else if (!running) {
        running = true;
        rafId = requestAnimationFrame(render);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    rafId = requestAnimationFrame(render);

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        // glassmorphism のためのベース。中央は暗くしてパーティクル球の
        // コントラストを保ちつつ、隅に向けて冷色系の色味を持たせる。
        background: "radial-gradient(ellipse at 50% 50%, #050a16 0%, #04050d 55%, #060914 100%)",
      }}
    >
      {/* 単色 (ティール) の大きくぼかしたブロブを 3 つ配置し、
          CSS keyframes でゆっくり位置・スケールを変えることで
          色が背景を波打つように流れるエフェクトを作る。
          glass カードの blur がこの単色の濃淡を拾う。 */}
      <Box
        sx={{
          position: "absolute",
          width: "70%",
          height: "70%",
          top: "-15%",
          left: "-15%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(95, 170, 162, 0.55) 0%, transparent 65%)",
          filter: "blur(80px)",
          pointerEvents: "none",
          willChange: "transform",
          animation: "fdt-wave-a 22s ease-in-out infinite",
          "@keyframes fdt-wave-a": {
            "0%, 100%": { transform: "translate(0%, 0%) scale(1)" },
            "33%": { transform: "translate(25%, 15%) scale(1.15)" },
            "66%": { transform: "translate(40%, 35%) scale(0.9)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "65%",
          height: "65%",
          top: "30%",
          left: "55%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(95, 170, 162, 0.45) 0%, transparent 65%)",
          filter: "blur(90px)",
          pointerEvents: "none",
          willChange: "transform",
          animation: "fdt-wave-b 28s ease-in-out infinite",
          "@keyframes fdt-wave-b": {
            "0%, 100%": { transform: "translate(0%, 0%) scale(1)" },
            "40%": { transform: "translate(-30%, -25%) scale(0.85)" },
            "75%": { transform: "translate(-15%, 20%) scale(1.2)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "55%",
          height: "55%",
          top: "55%",
          left: "0%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(95, 170, 162, 0.4) 0%, transparent 65%)",
          filter: "blur(85px)",
          pointerEvents: "none",
          willChange: "transform",
          animation: "fdt-wave-c 34s ease-in-out infinite",
          "@keyframes fdt-wave-c": {
            "0%, 100%": { transform: "translate(0%, 0%) scale(1)" },
            "50%": { transform: "translate(45%, -55%) scale(1.1)" },
          },
        }}
      />
      <Box
        component="canvas"
        ref={canvasRef}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    </Box>
  );
}
